import React, { useState } from 'react';
import { 
  X, 
  BarChart3, 
  DollarSign, 
  TrendingUp, 
  Wallet, 
  FileText, 
  Percent, 
  Building2,
  Calculator,
  ChevronRight,
  Check,
  Star
} from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { useDashboardStore } from '../../../stores/dashboardStore';
import { 
  KPI_LABELS, 
  RATIO_LABELS, 
  SYSCOHADA_CLASSES,
  type WidgetType,
  type KPIMetric,
  type RatioType,
  type WidgetSize
} from '../../../types/dashboard-widgets';

interface AddWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface WidgetTemplate {
  type: WidgetType;
  icon: React.ElementType;
  title: string;
  description: string;
  defaultSize: WidgetSize;
  hasConfig?: boolean;
  configOptions?: {
    type: 'kpi' | 'ratio' | 'account-class' | 'account';
  };
}

const WIDGET_TEMPLATES: WidgetTemplate[] = [
  {
    type: 'credit_score',
    icon: Star,
    title: 'Cote de Crédit',
    description: 'Score ML et notation financière Wanzo',
    defaultSize: 'md'
  },
  {
    type: 'kpi',
    icon: TrendingUp,
    title: 'Indicateur KPI',
    description: 'Affiche une métrique clé avec tendance',
    defaultSize: 'sm',
    hasConfig: true,
    configOptions: { type: 'kpi' }
  },
  {
    type: 'balance',
    icon: BarChart3,
    title: 'Bilan',
    description: 'Vue résumée du bilan comptable',
    defaultSize: 'md'
  },
  {
    type: 'pnl',
    icon: DollarSign,
    title: 'Compte de Résultat',
    description: 'Produits, charges et résultat net',
    defaultSize: 'md'
  },
  {
    type: 'cash',
    icon: Wallet,
    title: 'Trésorerie',
    description: 'Position de trésorerie détaillée',
    defaultSize: 'md'
  },
  {
    type: 'tax',
    icon: FileText,
    title: 'Fiscalité',
    description: 'Obligations fiscales et TVA',
    defaultSize: 'md'
  },
  {
    type: 'ratio',
    icon: Percent,
    title: 'Ratio Financier',
    description: 'Ratio avec interprétation',
    defaultSize: 'sm',
    hasConfig: true,
    configOptions: { type: 'ratio' }
  },
  {
    type: 'account',
    icon: Building2,
    title: 'Compte / Classe',
    description: 'Suivre un compte ou une classe SYSCOHADA',
    defaultSize: 'md',
    hasConfig: true,
    configOptions: { type: 'account-class' }
  },
  {
    type: 'metrics',
    icon: Calculator,
    title: 'Métriques SYSCOHADA',
    description: 'Vue des classes comptables',
    defaultSize: 'md'
  }
];

export function AddWidgetModal({ isOpen, onClose }: AddWidgetModalProps) {
  const { addWidget } = useDashboardStore();
  const [selectedTemplate, setSelectedTemplate] = useState<WidgetTemplate | null>(null);
  const [step, setStep] = useState<'select' | 'configure'>('select');
  const [config, setConfig] = useState<any>({});

  const handleSelectTemplate = (template: WidgetTemplate) => {
    setSelectedTemplate(template);
    setConfig({});
    
    if (template.hasConfig) {
      setStep('configure');
    } else {
      // Add widget directly
      addWidget({ type: template.type, title: template.title, size: template.defaultSize, config: {} });
      handleClose();
    }
  };

  const handleAddWidget = () => {
    if (!selectedTemplate) return;
    
    let title = selectedTemplate.title;
    
    // Customize title based on config
    if (selectedTemplate.type === 'kpi' && config.metric) {
      title = KPI_LABELS[config.metric as KPIMetric]?.label || title;
    } else if (selectedTemplate.type === 'ratio' && config.ratioType) {
      title = RATIO_LABELS[config.ratioType as RatioType]?.label || title;
    } else if (selectedTemplate.type === 'account' && config.accountClass != null) {
      const classInfo = SYSCOHADA_CLASSES.find(c => c.code === String(config.accountClass));
      title = classInfo?.name || `Classe ${config.accountClass}`;
    }
    
    addWidget({ type: selectedTemplate.type, title, size: selectedTemplate.defaultSize, config });
    handleClose();
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setStep('select');
    setConfig({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'select' ? 'Ajouter un Widget' : `Configurer : ${selectedTemplate?.title}`}
      size="lg"
      noPadding
    >
      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[60vh]">
        {step === 'select' ? (
          <div className="grid grid-cols-2 gap-3">
            {WIDGET_TEMPLATES.map((template) => {
              const Icon = template.icon;
              return (
                <button
                  key={template.type}
                  onClick={() => handleSelectTemplate(template)}
                  className="flex items-start gap-3 p-4 text-left border border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all group"
                >
                  <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{template.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {template.description}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors mt-1" />
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Back button */}
            <button
              onClick={() => setStep('select')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Retour à la sélection
            </button>

            {/* KPI Config */}
            {selectedTemplate?.configOptions?.type === 'kpi' && (
              <div className="space-y-3">
                <label className="text-sm font-medium">Sélectionnez la métrique</label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(KPI_LABELS) as KPIMetric[]).map((metric) => (
                    <button
                      key={metric}
                      onClick={() => setConfig({ ...config, metric, showTrend: true })}
                      className={`
                        flex items-center justify-between p-3 text-left text-sm border rounded-lg transition-all
                        ${config.metric === metric 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-border hover:border-primary/50'
                        }
                      `}
                    >
                      <span>{KPI_LABELS[metric].label}</span>
                      {config.metric === metric && <Check className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Ratio Config */}
            {selectedTemplate?.configOptions?.type === 'ratio' && (
              <div className="space-y-3">
                <label className="text-sm font-medium">Sélectionnez le ratio</label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(RATIO_LABELS) as RatioType[]).map((ratio) => (
                    <button
                      key={ratio}
                      onClick={() => setConfig({ ...config, ratioType: ratio, showInterpretation: true })}
                      className={`
                        flex items-center justify-between p-3 text-left text-sm border rounded-lg transition-all
                        ${config.ratioType === ratio 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-border hover:border-primary/50'
                        }
                      `}
                    >
                      <span>{RATIO_LABELS[ratio].label}</span>
                      {config.ratioType === ratio && <Check className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Account Class Config */}
              {selectedTemplate?.configOptions?.type === 'account-class' && (
                <div className="space-y-3">
                  <label className="text-sm font-medium">Sélectionnez une classe SYSCOHADA</label>
                  <div className="grid grid-cols-1 gap-2">
                    {SYSCOHADA_CLASSES.map((classInfo) => (
                      <button
                        key={classInfo.code}
                        onClick={() => setConfig({ ...config, accountClass: classInfo.code })}
                        className={`
                          flex items-center justify-between p-3 text-left border rounded-lg transition-all
                          ${config.accountClass === classInfo.code 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50'
                          }
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`
                            w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold
                            ${classInfo.type === 'asset' || classInfo.type === 'expense'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            }
                          `}>
                            {classInfo.code}
                          </span>
                          <div>
                            <p className="font-medium">{classInfo.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {classInfo.type === 'asset' || classInfo.type === 'expense' ? 'Emplois' : 'Ressources'}
                            </p>
                          </div>
                        </div>
                        {config.accountClass === classInfo.code && <Check className="h-4 w-4 text-primary" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Footer */}
      {step === 'configure' && (
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleAddWidget}
            disabled={
              (selectedTemplate?.configOptions?.type === 'kpi' && !config.metric) ||
              (selectedTemplate?.configOptions?.type === 'ratio' && !config.ratioType) ||
              (selectedTemplate?.configOptions?.type === 'account-class' && !config.accountClass)
            }
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Ajouter le widget
          </button>
        </div>
      )}
    </Modal>
  );
}
